/**
 * Proxy Contract — Cognitive Signals (FG5: Rigide Cognitieve Detectie)
 *
 * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.A + §FG5.
 *
 * THE PROBLEM
 * ===========
 * Two detectors evolved unevenly:
 *   - `dokAutoTagger.ts`  — already STRUCTURE-driven (type baseline + ceiling
 *     clamp + multi-step signals). Robust.
 *   - `cognitiveLevel.ts` — still leans on natural-language regex and falls
 *     back to `unknown` on short, terse DOK-3 questions.
 *
 * THE CONTRACT
 * ============
 * This module is the SINGLE source of truth for the STRUCTURAL cognitive
 * signal of a question type: a `{ floor, ceiling }` pair. Both detectors
 * consume the same facts, so DOK and Bloom can no longer drift apart. Regex
 * is demoted to a tiebreaker WITHIN the [floor, ceiling] window.
 *
 * Every heuristic that uses this returns a CONFIDENCE score (anti-bandaid
 * principle #5): heuristics never pretend to be truth, downstream picks the
 * threshold.
 */

import type { QuestionType, DOKLevel } from '../../content/types'

/** Coarse cognitive tier shared between DOK (1-4) and Bloom-style labels. */
export type CognitiveTier = 'recall' | 'application' | 'strategic' | 'extended'

export interface CognitiveBound {
  /** The cognitive tier this bound represents. */
  tier: CognitiveTier
  /** Numeric DOK equivalent for clamping/comparison. */
  dok: DOKLevel
  /** Human-readable reason for observability. */
  reason: string
}

export interface CognitiveBounds {
  /** Lowest cognitive tier the question type can structurally be. */
  floor: CognitiveBound
  /** Highest cognitive tier the question type can structurally be. */
  ceiling: CognitiveBound
}

const TIER_TO_DOK: Record<CognitiveTier, DOKLevel> = {
  recall: 1,
  application: 2,
  strategic: 3,
  extended: 4,
}

/**
 * Structural floor per question type. A type's INHERENT minimum cognitive
 * demand — e.g. a `step_solver` is at least application regardless of text.
 * Consolidated from dokAutoTagger's DOK*_TYPES sets.
 */
const TYPE_FLOOR: Partial<Record<QuestionType, CognitiveTier>> = {
  true_false_series: 'recall',
  clock: 'recall',
  clock_set: 'application',
  categorize: 'application',
  ordering: 'application',
  matching: 'application',
  error_spotting: 'application',
  sentence_parser: 'application',
  comparison_judge: 'application',
  verb_conjugation_flow: 'application',
  step_solver: 'strategic',
  strategy_solver: 'strategic',
}

/**
 * Structural ceiling per question type — the MAXIMUM cognitive demand the
 * type can express. Aligns with the per-type `cognitiveCeiling` sidecar used
 * by dokAutoTagger. Types absent here default to `extended` (no clamp).
 */
const TYPE_CEILING: Partial<Record<QuestionType, CognitiveTier>> = {
  true_false_series: 'application',
  clock: 'application',
  multiple_choice: 'extended',
  step_solver: 'strategic',
  strategy_solver: 'strategic',
}

function makeBound(tier: CognitiveTier, reason: string): CognitiveBound {
  return { tier, dok: TIER_TO_DOK[tier], reason }
}

/**
 * Resolve the structural `{ floor, ceiling }` for a question type. This is
 * the shared fact both `dokAutoTagger` and `cognitiveLevel` should build on.
 */
export function getCognitiveBoundsForType(type: QuestionType): CognitiveBounds {
  const floorTier = TYPE_FLOOR[type] ?? 'recall'
  const ceilingTier = TYPE_CEILING[type] ?? 'extended'

  // Guard: a misconfigured floor must never exceed the ceiling.
  const floorDok = TIER_TO_DOK[floorTier]
  const ceilingDok = TIER_TO_DOK[ceilingTier]
  const safeCeilingTier = ceilingDok < floorDok ? floorTier : ceilingTier

  return {
    floor: makeBound(floorTier, `type "${type}" floor`),
    ceiling: makeBound(safeCeilingTier, `type "${type}" ceiling`),
  }
}

/** Clamp a candidate cognitive tier into the structural [floor, ceiling] window. */
export function clampToCognitiveBounds(
  candidate: CognitiveTier,
  bounds: CognitiveBounds,
): { tier: CognitiveTier; clamped: boolean; reason?: string } {
  const candidateDok = TIER_TO_DOK[candidate]
  if (candidateDok < bounds.floor.dok) {
    return {
      tier: bounds.floor.tier,
      clamped: true,
      reason: `raised to ${bounds.floor.tier} (${bounds.floor.reason})`,
    }
  }
  if (candidateDok > bounds.ceiling.dok) {
    return {
      tier: bounds.ceiling.tier,
      clamped: true,
      reason: `lowered to ${bounds.ceiling.tier} (${bounds.ceiling.reason})`,
    }
  }
  return { tier: candidate, clamped: false }
}

export { TIER_TO_DOK }
